<script setup lang="ts">
import { api } from '@/api';
import { type QaEquipmentRtSystemDto } from '@/api/generator';
import VeeSubmitButton from '@/components/forms/SubmitButton/VeeSubmitButton.vue';
import { useVeeForm } from '@/components/forms/useVeeForm';
import BasePage from '@/components/pages/BasePage/BasePage.vue';
import BasePageStatus from '@/components/pages/BasePage/BasePageStatus.vue';
import BasePageTitle from '@/components/pages/BasePage/BasePageTitle.vue';
import { routenames } from '@/router/routenames';
import { useQaEquipmentStore } from '@/stores/qaEquipmentStore.ts';
import { I18nKey } from '@/utils/typings';
import { useLoading } from '@/utils/useLoading';
import { useToken } from '@/utils/tokens/tokens';
import { reloadEquipmentKey } from '@/views/qaEquipment/types.ts';
import {
  getManufacturerLabel,
  getModelLabel,
  getTypeLabel,
  isRegistrablePtwQaEquipment,
} from '@/views/qaEquipment/utils';
import { useTranslation } from 'i18next-vue';
import { onMounted, provide } from 'vue';
import { useRouter } from 'vue-router';
import QaEquipmentForm from './components/QaEquipmentForm.vue';
import { toCreateDto } from './mappings';
import PtwCancelButton from '@/components/buttons/PtwCancelButton.vue';
import { backendValidator } from '@/validation/validators/backendValidator';
import { entityNotFoundErrorHandler, formBackendErrorHandler } from '@/errorHandling';

interface EntitiesEditViewProps {
  id: string;
}

const props = defineProps<EntitiesEditViewProps>();
const router = useRouter();
const { t } = useTranslation();
const qaEquipmentStore = useQaEquipmentStore();

const {
  action: loadEquipment,
  actionWithoutLoading: silentLoading,
  loading,
} = useLoading(async () => {
  const { result } = await api.qaEquipmentApi.getQaEquipmentRtSystem(
    {
      id: props.id,
    },
    [entityNotFoundErrorHandler(router)],
  );

  if (result) {
    resetForm({ values: result });
  }
}, true);

// Allow the Dialog to reload the equipment after the de-/registration
const reloadEquipment = () => {
  loadEquipment();
};

provide(reloadEquipmentKey, reloadEquipment);

const { form } = useVeeForm<QaEquipmentRtSystemDto>({
  schemaName: 'QaEquipmentRtSystemDto',
  mode: 'edit',
  customValidateFunctions: [
    backendValidator((qaEquipmentDto, errorHandlers) =>
      api.qaEquipmentApi.validateQaEquipmentRtSystem(
        {
          createQaEquipmentRtSystemDto: toCreateDto(qaEquipmentDto),
        },
        errorHandlers,
      ),
    ),
  ],
});

const { handleSubmit, resetForm, values } = form;

const token = useToken(values);

onMounted(async () => {
  await silentLoading();
  await token.request();
});

token.on.initialized(loadEquipment);

token.on.inUse(() => {
  router.push({ name: routenames.qaEquipment.overview });
});

const submit = handleSubmit(
  async (qaEquipmentRtSystem) => {
    const { success, result } = await api.qaEquipmentApi.updateQaEquipmentRtSystem(
      {
        id: qaEquipmentRtSystem.id,
        createQaEquipmentRtSystemDto: toCreateDto(qaEquipmentRtSystem),
      },
      [formBackendErrorHandler(form)],
    );
    if (success && result) {
      qaEquipmentStore.setDeviceForRegistration(
        isRegistrablePtwQaEquipment(result) &&
          (result.registrationState === 'NotRegistered' ||
            result.registrationState === 'DeRegistered')
          ? result
          : undefined,
      );
      await router.push({
        name: routenames.qaEquipment.index,
      });
    }
  },
  {
    withReferenceCheck: true,
  },
);
</script>

<template>
  <form @submit="submit">
    <BasePage :loading="loading">
      <template #title>
        <BasePageTitle
          :title="values.name"
          :titleId="values.iuid"
          :subtitle="`${t(getTypeLabel(values) as I18nKey)} | ${t(
            getManufacturerLabel(values) as I18nKey,
          )} | ${getModelLabel(values)}`"
        />
      </template>

      <template #status>
        <BasePageStatus :entity="values" />
      </template>

      <template #actions>
        <PtwCancelButton :to="{ name: routenames.qaEquipment.overview }" />
        <VeeSubmitButton class="ml-4" />
      </template>
      <QaEquipmentForm />
    </BasePage>
  </form>
</template>
